import type { Board } from "../../board";
import { getMobGoldValue } from "../../economy/mobGoldValues";
import type { DamageType } from "../../damageType";
import { AirBlastShot } from "../../projectiles/airBlastShot";
import { CryoShot } from "../../projectiles/cryoShot";
import { Missile } from "../../projectiles/missile";
import { PlasmaBall } from "../../projectiles/plasmaBall";
import { SolidifierShot } from "../../projectiles/solidifierShot";
import { deserializeXY } from "../../serialization";
import type { Thing } from "../../thing";
import type { XY } from "../../xy";
import { Sound } from "../sounds/sound";
import { SoundId } from "../sounds/soundId";
import { MobSpawnerConfig } from "./mobSpawnerConfig";
import { Portal2 } from "./portal2";
import { Portal2Part } from "./portal2Part";

export class ReinforcedPortal2 extends Portal2 {
	static key = "ReinforcedPortal2";
	constructor(xy: XY) {
		super(xy);
	}

	override getBaseGoldValue(): number {
		return getMobGoldValue(ReinforcedPortal2.key);
	}

	override onProjectileHit(projectile: unknown): void {
		if (projectile instanceof SolidifierShot) return;
		if (projectile instanceof PlasmaBall) return;
		if (projectile instanceof AirBlastShot) return;
		if (projectile instanceof CryoShot) return;
		this.board!.addNonPhysicalThing(new Sound(SoundId.METAL_BANG, this.xy));
	}

	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		// Only take damage from null source (kill()) or Missile
		if (source === null || source instanceof Missile) {
			return super.applyDamage(type, amount, source);
		}
		return 0;
	}

	static override create(board: Board, targetXY: XY): ReinforcedPortal2 | null {
		const neighbors = targetXY.getNeighbors();
		for (const xy of neighbors) {
			if (!board.isInBounds(xy) || !board.isPassable(xy)) return null;
			if (board.getThingsAt(xy).length > 0) return null;
		}
		const portal = new ReinforcedPortal2(targetXY);
		board.addThing(portal);
		for (const xy of neighbors) {
			const part = new Portal2Part(xy);
			board.addThing(part);
			portal.addPart(part);
		}
		return portal;
	}

	static override deserialize(data: any): ReinforcedPortal2 {
		const p = new ReinforcedPortal2(deserializeXY(data.xy));
		p.restoreBaseFields(data);
		if (data.spawnProbability !== undefined) p.spawnProbability = data.spawnProbability;
		if (data.mobSpawnerConfig) p.setMobSpawnerConfig(MobSpawnerConfig.deserialize(data.mobSpawnerConfig));
		p.gold = data.gold ?? 0;
		return p;
	}
}
